import * as Sentry from "@sentry/react";
import { env } from './environment';

export function initSentry() {
  // Use environment configuration
  const sentryDsn = env.sentryDsn;
  const shouldEnableSentry = env.enableSentry;
  
  if (sentryDsn && shouldEnableSentry) {
    Sentry.init({
      dsn: sentryDsn,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          // Mask sensitive text in replays
          maskAllText: false,
          maskAllInputs: true,
          // Record console logs in replays
          recordCanvas: true,
          // Sample console logs
          logLevel: 'info',
        }),
        // Add breadcrumbs for better debugging
        Sentry.breadcrumbsIntegration({
          console: true,
          dom: true,
          fetch: true,
          history: true,
          xhr: true,
        }),
      ],
      // Performance Monitoring
      tracesSampleRate: env.isProduction ? 0.1 : 1.0, // 10% in production, 100% in development
      // Session Replay
      replaysSessionSampleRate: env.isProduction ? 0.1 : 0, // 10% in production, 0% in development
      replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
      // Release tracking
      environment: env.name,
      release: `bloom-react@${import.meta.env.VITE_APP_VERSION || '1.0.0'}`,
      // Better debugging options
      debug: env.enableDebug, // Enable debug mode based on environment
      attachStacktrace: true, // Always attach stack traces
      autoSessionTracking: true, // Track user sessions
      // Configure breadcrumbs
      maxBreadcrumbs: 50, // Keep more breadcrumbs for debugging
      beforeBreadcrumb(breadcrumb, hint) {
        // Filter out noisy breadcrumbs
        if (breadcrumb.category === 'console' && breadcrumb.level === 'debug') {
          return null;
        }
        return breadcrumb;
      },
      beforeSend(event, hint) {
        // Add user context
        const user = Sentry.getCurrentScope().getUser();
        if (!user?.email && window.localStorage.getItem('bloom_user_email')) {
          Sentry.setUser({
            email: window.localStorage.getItem('bloom_user_email'),
            id: window.localStorage.getItem('bloom_user_id'),
          });
        }
        
        // Filter out sensitive data
        if (event.request?.cookies) {
          delete event.request.cookies;
        }
        
        // Add custom context
        event.contexts = {
          ...event.contexts,
          app: {
            build_time: new Date().toISOString(),
            viewport: {
              width: window.innerWidth,
              height: window.innerHeight,
            },
          },
        };
        
        return event;
      },
    });
    
    console.log(`Sentry initialized for ${env.name} environment`);
  } else {
    console.log('Sentry disabled in this environment');
  }
}

export const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter;

// Function to set user context in Sentry
export function setSentryUser(user: { id: string; email: string; username?: string } | null) {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.username,
    });
  } else {
    Sentry.setUser(null);
  }
}